import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Fuse from 'fuse.js'

import { Dialog } from 'primereact/dialog'
import { RadioButton } from 'primereact/radiobutton'
import { TabPanel, TabView } from 'primereact/tabview'

import { AutoCompleteField, Button, DropDownField, MultiSelectField, Table, TextInputField } from '../../../helpers'

import Loading from '../../../common/Loading'
import CoverageDetailsNew from './CoverageDetailsNew'

import { searchCompanies } from '../../../services/api/company'
import { getBranchListByCompany } from '../../../services/api/companyBranch'
import { getMaterialMasterAndTempMaterialIds } from '../../../services/api/materialMaster'
import { getVendorRateContractItemsByVendorRateContractId } from '../../../services/api/vendorRateContractItem'
import { deleteVendorRateContractById, getVendorRateContractById, insertVendorRateContract } from '../../../services/api/vendorRateContract'

import { raiseToast } from '../../../services/redux/reducers/toast/toast'
import { fetchMaterialBrands, fetchMaterialCategories, fetchVendorRCCoverageTypes, fetchVendorRCTypes } from '../../../services/redux/reducers/commonThunk'
import { updateMaterialDetails, updateSearchOptions, updateSearchParams, updateVendorRateContractItems } from '../../../services/redux/reducers/vendorRateContractItems/vendorRateContractItems'


const PriceAndGeoMappings = (props) => {
    const { visibleAddVendorRateContract, setVisibleAddVendorRateContract, vendorRateContractId } = props

    const [isLoading, setIsLoading] = useState(false)
    const [isEditable, setIsEditable] = useState(true)
    const [isItemsEditable, setIsItemsEditable] = useState(true)
    const [isCoverageDetailsVisible, setIsCoverageDetailsVisible] = useState(false)

    const [selectedBranchId, setSelectedBranchId] = useState(null)
    const [selectedPMBranches, setSelectedPMBranches] = useState([])

    const [initialVendorRateContract, setInitialVendorRateContract] = useState({})
    const [initialVendorRateContractItems, setInitialVendorRateContractItems] = useState([])
    const [vendorRateContract, setVendorRateContract] = useState({})
    const [vendorRateContractItems, setVendorRateContractItems] = useState([])
    const [materialDetails, setMaterialDetails] = useState([])
    const [sellerCompanies, setSellerCompanies] = useState([])
    const [sellerCompaniesSuggestions, setSellerCompaniesSuggestions] = useState([])
    const [sellerCompanyBranches, setSellerCompanyBranches] = useState([])

    const userDetails = useSelector(state => state.userDetails)
    const companyPMBranches = userDetails.PMCompanyDetails.companyBranches

    const vendorRCTypes = useSelector(state => state.common.vendorRCTypes)
    const vendorRCCoverageTypes = useSelector(state => state.common.vendorRCCoverageTypes)
    const materialBrands = useSelector(state => state.common.materialBrands)
    const materialCategories = useSelector(state => state.common.materialCategories)
    
    const dispatch = useDispatch()

    const companyFuzzSearch = useCallback(new Fuse(sellerCompanies, {
        keys: ['companyName'],
        includeScore: true,
        threshold: 0.5,
        ignoreLocation: true,
        shouldSort: true,
        getFn: (obj) => {
            return obj.companyName.replace(/\s+/g, '')
        },
    }), [sellerCompanies])

    useEffect(() => {
        dispatch(fetchMaterialBrands())
        dispatch(fetchMaterialCategories())
        dispatch(fetchVendorRCTypes())
        dispatch(fetchVendorRCCoverageTypes())
    }, [])

    useEffect(() => {
        getSellerCompanies()
    }, [])

    useEffect(() => {
        if (vendorRateContract && typeof(vendorRateContract.companyId) === "number"){
            getSellerCompanyBranches(vendorRateContract.companyId)
        }
    }, [vendorRateContract])

    useEffect(() => {
        if (vendorRateContractId){
            getVendorRateContractDetails()
        }else {
            setIsItemsEditable(false)
        }
    }, [vendorRateContractId])

    const getVendorRateContractDetails = async () => {
        setIsLoading(true)
        const vendorRateContractRes = await getVendorRateContractById(vendorRateContractId)
        // console.log("vendorRateContractRes", vendorRateContractRes)

        if (vendorRateContractRes.failure){
            setIsLoading(false)
            return dispatch(raiseToast({severity:'error', detail: vendorRateContractRes.data.error, summary: 'Vendor Rate Contract are not fetched.'}))
        }

        const vendorRateContractItemsRes = await getVendorRateContractItemsByVendorRateContractId(vendorRateContractId)
        // console.log("vendorRateContractItemsRes", vendorRateContractItemsRes)

        if (vendorRateContractItemsRes.failure){
            setIsLoading(false)
            return dispatch(raiseToast({severity:'error', detail: vendorRateContractItemsRes.data.error, summary: 'Vendor Rate Contract Items are not fetched.'})) 
        }

        // let updatedGeoMap = getValuesTransformedToKeysObject(vendorRateContractRes.data.geoMap)
        let updatedVendorRateContract = {...vendorRateContractRes.data}

        const materialIds = []
        vendorRateContractItemsRes.data.map(eachVendorRateContractItem => {
            if (eachVendorRateContractItem.statusId === 1){
                if (eachVendorRateContractItem.materialId){
                    materialIds.push(eachVendorRateContractItem.materialId)
                }
            }
        })

        let materialDetailsRes
        if (materialIds.length){
            materialDetailsRes = await getMaterialMasterAndTempMaterialIds({materialIds: materialIds})
            // console.log("materialDetailsRes", materialDetailsRes)

            if (materialDetailsRes.failure){
                setIsLoading(false)
                return dispatch(raiseToast({severity:'error', detail: materialDetailsRes.data.error, summary:'Material Details are not fetched.', life: 3000}))
            }
            setMaterialDetails(materialDetailsRes.data)
        }

        getSellerCompanyBranches(vendorRateContractRes.data.companyId)

        setVendorRateContract(updatedVendorRateContract)
        setVendorRateContractItems(vendorRateContractItemsRes.data)
        setInitialVendorRateContract(updatedVendorRateContract)
        setInitialVendorRateContractItems(vendorRateContractItemsRes.data)

        setIsItemsEditable(false)
        setIsCoverageDetailsVisible(true)
        setIsEditable(false)
        setIsLoading(false)
    }

    const getSellerCompanies = async () => {
        const sellerCompaniesRes = await searchCompanies({companyType: 'seller', paginator: false})
        // console.log("sellerCompaniesRes", sellerCompaniesRes)

        if (sellerCompaniesRes.failure){
            return dispatch(raiseToast({ severity: 'error', summary: 'Seller Company', detail: sellerCompaniesRes.data.error, life: 3000 }))
        }

        setSellerCompanies(sellerCompaniesRes.data.data)
    }

    const getSellerCompanyBranches = async (companyId) => {
        const sellerCompaniesBranchesRes = await getBranchListByCompany(companyId)
        // console.log("sellerCompaniesBranchesRes", sellerCompaniesBranchesRes)

        if (sellerCompaniesBranchesRes.failure){
            return dispatch(raiseToast({ severity: 'error', summary: 'Seller Company Branch', detail: sellerCompaniesBranchesRes.data.error, life: 3000 }))
        }

        setSellerCompanyBranches(sellerCompaniesBranchesRes.data)
    }

    const companySearch = (event) => {
        if (event.query === "") {
            setSellerCompaniesSuggestions([...sellerCompanies])
        }else {
            const searchResults = companyFuzzSearch.search(event.query)
            const relatedCompanies = searchResults.map(eachProduct => eachProduct.item)
            setSellerCompaniesSuggestions(relatedCompanies)
        }
    }

    const handleCloseVendorRateContractModal = () => {
        setVisibleAddVendorRateContract(false)
        setIsEditable(true)
        setIsItemsEditable(true)
        setIsCoverageDetailsVisible(false)
        setVendorRateContract({})
        setVendorRateContractItems([])
        setInitialVendorRateContract({})
        setInitialVendorRateContractItems([])
        setSelectedPMBranches([])
        setSellerCompanyBranches([])
        dispatch(updateVendorRateContractItems([]))
        dispatch(updateSearchOptions([]))
        dispatch(updateSearchParams({dataType: "vendorRateContractItemsData", searchBy: "", searchText: "", statusIds: [1], dateType: "createdAt"}))
        dispatch(updateMaterialDetails([]))
    }

    const handleCompanyChange = (event) => {
        if (typeof(event.target.value) === "object"){
            setVendorRateContract(prevState => ({...prevState, companyId: event.target.value?.companyId}))
        }else{
            setVendorRateContract(prevState => ({...prevState, companyId: event.target.value}))
        }

        setSelectedPMBranches([])
        setSelectedBranchId(null)
        setVendorRateContract(prevState => ({...prevState, geoMapType: "", geoMap: {}}))
    }

    const handleVendorRCTypeChange = (event) => {
        let relatedVendorRCType = vendorRCTypes.find(eachVendorRCType => eachVendorRCType.vendorRCType === event.target.value)
        let relatedRCCoverageType = {}
        if (vendorRateContract.vendorRCCoverageTypeId){
            relatedRCCoverageType = vendorRCCoverageTypes.find(eachVendorRCCoverageType => eachVendorRCCoverageType.vendorRCCoverageTypeId === vendorRateContract.vendorRCCoverageTypeId)
        }
        if (initialVendorRateContract.vendorRCTypeId && initialVendorRateContract.vendorRCTypeId !== relatedVendorRCType.vendorRCTypeId){
            setVendorRateContractItems([])
        }else{
            setVendorRateContractItems(initialVendorRateContractItems)
        }
        
        if (relatedVendorRCType.vendorRCTypeId === 1){
            if (relatedRCCoverageType.vendorRCCoverageTypeId && relatedRCCoverageType.vendorRCCoverageTypeId === 4){
                return dispatch(raiseToast({ severity: 'error', detail: 'Selected RC type is not applicable for the selected RC Coverage.', life: 3000 }))
            }else{
                setVendorRateContract(prevState => ({...prevState, "vendorRCTypeId": relatedVendorRCType.vendorRCTypeId}))
            }
        }else{
            setVendorRateContract(prevState => ({...prevState, "vendorRCTypeId": relatedVendorRCType.vendorRCTypeId, "vendorRCCoverageTypeId": 4}))
        }
    }

    const handleVendorRCCoverageTypeChange = (event) => {
        let relatedVendorRCType = {}
        if (vendorRateContract.vendorRCTypeId){
            relatedVendorRCType = vendorRCTypes.find(eachVendorRCType => eachVendorRCType.vendorRCTypeId === vendorRateContract.vendorRCTypeId)
        }

        let relatedRCCoverageType = vendorRCCoverageTypes.find(eachVendorRCCoverageType => eachVendorRCCoverageType.vendorRCCoverageType === event.target.value)

        if (initialVendorRateContract.vendorRCCoverageTypeId && initialVendorRateContract.vendorRCCoverageTypeId !== relatedRCCoverageType.vendorRCCoverageTypeId){
            setVendorRateContractItems([])
        }else{
            setVendorRateContractItems(initialVendorRateContractItems)
        }

        if (relatedVendorRCType.vendorRCTypeId === 1){
            setVendorRateContract(prevState => ({...prevState, "vendorRCCoverageTypeId": relatedRCCoverageType.vendorRCCoverageTypeId}))
        }else {
            if (relatedRCCoverageType.vendorRCCoverageTypeId === 4){
                setVendorRateContract(prevState => ({...prevState, "vendorRCCoverageTypeId": relatedRCCoverageType.vendorRCCoverageTypeId}))
            }else{
                return dispatch(raiseToast({ severity: 'error', detail: 'Selected RC Coverage is not applicable for the selected RC Type.', life: 3000 }))
            }
        }
    }

    const handleBranchChange = (event) => {
        if (vendorRateContract.geoMapType === "All India"){
            let newMap = {}
            let relatedPMBranchIds = companyPMBranches.map(eachCompanyBranch => eachCompanyBranch.branchId)
            newMap[event.target.value] = relatedPMBranchIds
            
            setSelectedBranchId(event.target.value)
            setSelectedPMBranches(companyPMBranches)
            setVendorRateContract(prevState => ({...prevState, geoMap: newMap}))
        }else {
            setSelectedBranchId(event.target.value)
        }
    }

    const handlePMBranchChange = (event) => {
        let existedMappedPMBranchIds = []
        Object.values(vendorRateContract.geoMap).map(eachMap => existedMappedPMBranchIds.push(...eachMap))
        
        if (existedMappedPMBranchIds?.includes(event.selectedOption.branchId)){
            return dispatch(raiseToast({ severity: 'error', detail: 'Selected Branch Mapping already done..', life: 3000 }))
        }else{
            const relatedBranches = companyPMBranches.filter(eachCompanyBranch => event.target.value.includes(eachCompanyBranch.branchId))
            setSelectedPMBranches(relatedBranches)
        }
    }

    const handleVendorRateContractChange = (event) => {
        setVendorRateContract(prevState => ({...prevState, [event.target.name]: event.target.value}))
    }

    const handleGeoMapType = (event) => {
        let sellerCompanyBranchId = null
        if (sellerCompanyBranches.length === 1){
            sellerCompanyBranchId = sellerCompanyBranches[0].branchId
        }
        if (event.target.value === "All India"){
            let relatedPMBranchIds = companyPMBranches.map(eachCompanyBranch => eachCompanyBranch.branchId)
       
            setSelectedBranchId(sellerCompanyBranchId)
            setSelectedPMBranches(companyPMBranches)

            if (sellerCompanyBranchId){
                let newMap = {}
                newMap[sellerCompanyBranchId] = relatedPMBranchIds
                setVendorRateContract(prevState => ({...prevState, geoMap: newMap, "geoMapType": event.target.value}))
            }else{
                setVendorRateContract(prevState => ({...prevState, geoMap: {}, "geoMapType": event.target.value}))
            }
        }else{
            setSelectedBranchId(sellerCompanyBranchId)
            setSelectedPMBranches([])
            setVendorRateContract(prevState => ({...prevState, "geoMapType": event.target.value, geoMap: {}}))
        }
    }

    const handleSaveVendorRateContract = async () => {
        if (!vendorRateContract.catalogueName){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please enter catalogue name.' }))
        }

        if (!vendorRateContract.vendorRCTypeId){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select vendor RC Type' }))
        }

        if (!vendorRateContract.companyId){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select seller company.' }))
        }

        if (!vendorRateContract.geoMapType){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select Geo Map Type' }))
        }

        if (!Object.values(vendorRateContract.geoMap).length){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select seller branch and procmart branch mapping' }))
        }

        if (!vendorRateContract.vendorRCCoverageTypeId){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select vendor RC Coverage Type' }))
        }

        let updatedVendorRateContract = {...vendorRateContract}
        if (!vendorRateContract.vendorRateContractId){
            updatedVendorRateContract = {...updatedVendorRateContract, isNew: true}
        }

        if (window.confirm("Do you want to save the details..?")){
            setVendorRateContract(updatedVendorRateContract)
            if (updatedVendorRateContract.isNew){
                setInitialVendorRateContract(JSON.parse(JSON.stringify(updatedVendorRateContract)))
            }

            let data = {
                vendorRateContract: updatedVendorRateContract,
                vendorRateContractItems: []
            }

            setIsLoading(true)
            const vendorRateContractInsertionRes = await insertVendorRateContract(data)

            if (vendorRateContractInsertionRes.failure){
                setIsLoading(false)
                return dispatch(raiseToast({severity:'error', detail: vendorRateContractInsertionRes.data.error, summary: 'Vendor Rate Contract is not Inserted', life: 3000}))
            }

            setIsLoading(false)
            setVendorRateContract(vendorRateContractInsertionRes.data.vendorRateContractRes)
            setInitialVendorRateContract(vendorRateContractInsertionRes.data.vendorRateContractRes)
            dispatch(raiseToast({severity:'success', detail: 'Vendor Rate Contract created Successfully', life: 3000}))
            setIsCoverageDetailsVisible(true)
            setIsItemsEditable(false)
            setIsEditable(false)
        }
    }

    const handleEditVendorRateContract = () => {
        setIsEditable(true)
    }

    const handleDiscardVendorRateContract = () => {
        if (window.confirm("Do you want to Discard the changes...?")){
            setIsEditable(false)
            setSelectedBranchId(null)
            setSelectedPMBranches([])
            setVendorRateContract(JSON.parse(JSON.stringify(initialVendorRateContract)))
            setVendorRateContractItems(initialVendorRateContractItems)
        }
    }

    const handleAddBranchMapping = () => {
        if (!selectedBranchId){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select seller branch' }))
        }

        if (!selectedPMBranches.length){
            return dispatch(raiseToast({ severity: 'error', detail: 'Please select PM Branches.' }))
        }

        let updatedGeoMaps = {...vendorRateContract.geoMap}
        let relatedGeoMapIds = updatedGeoMaps[selectedBranchId]
        if (relatedGeoMapIds?.length){
            let selectedPMBranchIds = selectedPMBranches.map(eachCompanyBranch => eachCompanyBranch.branchId)
            let updatedBranchIds = [...relatedGeoMapIds, ...selectedPMBranchIds]

            updatedGeoMaps[selectedBranchId] = updatedBranchIds
        }else{
            let selectedPMBranchIds = selectedPMBranches.map(eachCompanyBranch => eachCompanyBranch.branchId)
            updatedGeoMaps[selectedBranchId] = selectedPMBranchIds
        }
        let updatedVendorRateContract = {...vendorRateContract, geoMap: updatedGeoMaps}
        setVendorRateContract(updatedVendorRateContract)
        setSelectedBranchId(null)
        setSelectedPMBranches([])
    }

    const getSellerBranchBody = (row) => {
        let relatedSellerBranch = sellerCompanyBranches.find(eachCompanyBranch => eachCompanyBranch.branchId === parseInt(row))

        return (
            <>{relatedSellerBranch?.branchName}</>
        )
    }

    const getPMBranchesBody = (row) => {
        let relatedPMBranchIds = vendorRateContract.geoMap[row]

        let relatedPMBranches = companyPMBranches.filter(eachCompanyBranch => relatedPMBranchIds.includes(eachCompanyBranch.branchId))
        let relatedPMBranchNamesArr = relatedPMBranches.map(eachCompanyBranch => eachCompanyBranch.branchName)
        let relatedPMBranchNames = relatedPMBranchNamesArr.join(", ")

        return (
            <>{relatedPMBranchNames}</>
        )
    }

    const handleRemoveBranchMapping = (row) => {
        let updatedGeoMaps = vendorRateContract.geoMap
        delete updatedGeoMaps[row]
        setVendorRateContract(prevState => ({...prevState, geoMap: updatedGeoMaps}))
        setSelectedBranchId(null)
        setSelectedPMBranches([])
    }

    const handleDeleteVendorRateContract = async () => {
        if (window.confirm("Are you sure you want to delete the Vendor Rate Contract...?")){
            setIsLoading(true)
            let vendorRateContractDeleteRes = await deleteVendorRateContractById(vendorRateContract.vendorRateContractId)

            if (vendorRateContractDeleteRes.failure){
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'error', detail: 'Vendor Rate Contract is Not Deleted.', summary: vendorRateContractDeleteRes.data.error, life: 3000 }))
            }

            dispatch(raiseToast({ severity: 'success', detail: 'Vendor rate contract deleted successfully.', life: 3000 }))
            setVisibleAddVendorRateContract(false)
            setIsCoverageDetailsVisible(false)
            setVendorRateContract({})
            setInitialVendorRateContract({})
            setVendorRateContractItems([])
            setInitialVendorRateContractItems([])
            setIsEditable(true)
            setIsLoading(false)
        }
    }

    const getRemoveBody = (row) => {
        return (
            <Button className="decrement-btn-icon small-btn" icon="pi pi-minus-circle plus-icons" severity="danger" text onClick={() => handleRemoveBranchMapping(row)} disabled={isCoverageDetailsVisible && !isEditable}/>
        )
    }

    const tableColumns = [
        {
            header: 'Seller Branch',
            field: '',
            body: getSellerBranchBody
        },
        {
            header: 'PM Branches',
            field: '',
            body: getPMBranchesBody
        },
        {
            header: 'Remove Mapping',
            field: '',
            body: getRemoveBody
        }
    ]

    let companyName = vendorRateContract && vendorRateContract.companyId
    if (vendorRateContract && typeof (vendorRateContract.companyId) === "number") {
        companyName = sellerCompanies?.find(eachCompany => eachCompany.companyId === vendorRateContract.companyId)
    }

    let selectedPMBranchIds = selectedPMBranches.map(eachCompanyBranch => eachCompanyBranch.branchId)

    let header = 'New RC Catalogue'
    if (vendorRateContract && vendorRateContract.vendorRateContractId){
        header = 'Catalogue Name: ' + vendorRateContract.catalogueName
    }

    return (
        <>
            <Dialog header={header} className="pi-punch-dialog vendor-ratecontract-dialog" visible={visibleAddVendorRateContract} style={{ height: '85vh', width: '90vw', backgroundColor: '#f8f8f8' }} onHide={() => handleCloseVendorRateContractModal()} >
                <Loading visible={isLoading}/>
                <TabView>
                    <TabPanel header='Price and Geo Mappings' disabled={isItemsEditable && !vendorRateContract.isNew}>
                        <div className="flex flex-column md:flex-row mt-3">
                            <div className="flex flex-column col-12 md:col-2 pm-branch">
                                <TextInputField name="catalogueName" labelText="Catalogue Name" onChange={handleVendorRateContractChange} value={vendorRateContract.catalogueName} disabled={(isCoverageDetailsVisible && !isEditable) || vendorRateContract.vendorRateContractId}/>
                            </div>
                            <div className='flex flex-column md:flex-row mt-2 ml-2'>
                                <div><span className='text-xm' style={{ color: "#333", fontSize:"12px"}}>Rate Contract Type</span><span className="required-mark"></span> :</div>
                                <div className="flex flex-column ml-3">
                                    {
                                        vendorRCTypes.map(eachVendorRCType => {
                                            return (
                                                <div className="flex flex-row mr-2">
                                                    <RadioButton 
                                                        inputId={eachVendorRCType.vendorRCType} 
                                                        name={eachVendorRCType.vendorRCType} 
                                                        value={eachVendorRCType.vendorRCType} 
                                                        checked={vendorRateContract.vendorRCTypeId === eachVendorRCType.vendorRCTypeId} 
                                                        onChange={handleVendorRCTypeChange} 
                                                        disabled={(isCoverageDetailsVisible && !isEditable) || vendorRateContract.vendorRateContractId} // || (vendorRateContract.vendorRCCoverageTypeId === 4 && eachVendorRCType.vendorRCType === "Discount")
                                                    />
                                                    <label htmlFor={eachVendorRCType.vendorRCType} className="ml-1 text-xs" style={{cursor: 'pointer'}}>{eachVendorRCType.vendorRCType}</label>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="pb-3 my-4 pl-2">
                            <div className="grid">
                                <div className='flex flex-wrap col-12 md:col-12 lg:col-12 p-0'>
                                    <div className="flex flex-column col-12 md:col-2  lg:col-2 lg:mb-0 post-req-autocomplete company">
                                        <AutoCompleteField dropdown name="company" labelText="Seller Company" value={companyName} field="companyName" dropdownButton ClassName="styled-autocomplete-dropdown-button" searchMethod={companySearch} suggestions={sellerCompaniesSuggestions} onChange={handleCompanyChange} forceSelection required disabled={(isCoverageDetailsVisible && !isEditable) || vendorRateContract.vendorRateContractId }/>
                                    </div>
                                    <div className='flex flex-column md:flex-row mt-2 md:ml-3 ml-2 mr-3'>
                                        <div><span className='text-xm mb-3' style={{ color: "#333", fontSize:"12px"}}>Geo Map Type</span><span className="required-mark"></span> :</div>
                                        <div className='flex flex-column ml-2 mt-1'>
                                            <div className="flex flex-row mr-2">
                                                <RadioButton inputId="allIndia" name="All India" value="All India" onChange={handleGeoMapType} checked={vendorRateContract.geoMapType === "All India"} disabled={(isCoverageDetailsVisible && !isEditable) || !vendorRateContract.companyId}/>
                                                <label htmlFor="allIndia" className="ml-1 text-xs" style={{cursor: 'pointer'}}>All India</label>
                                            </div>
                                            <div className="flex flex-row mr-2">
                                                <RadioButton inputId="Vendor Company Branch" name="Vendor Branch Map" value="Vendor Branch Map" onChange={handleGeoMapType} checked={vendorRateContract.geoMapType === "Vendor Branch Map"} disabled={(isCoverageDetailsVisible && !isEditable) || !vendorRateContract.companyId}/>
                                                <label htmlFor="Vendor Company Branch" className="ml-1 text-xs" style={{cursor: 'pointer'}}>Vendor Branch Map</label>
                                            </div>
                                        </div>
                                    </div>
                                    {
                                        vendorRateContract.geoMapType &&
                                        <div className="flex flex-column col-12 md:col-3 lg:col-2 lg:mb-0 branch-input company-branch mt-2 md:mt-0">
                                            <DropDownField name="branchId" labelText="Branch" options={sellerCompanyBranches} optionLabel="branchName" optionValue="branchId" onChange={handleBranchChange} value={selectedBranchId} disabled={isCoverageDetailsVisible && !isEditable}/>
                                        </div>
                                    }
                                    {
                                        vendorRateContract.geoMapType && vendorRateContract.geoMapType === "Vendor Branch Map" &&
                                            <div className="flex flex-column col-12 md:col-2 lg:col-3 lg:mb-0">
                                                <MultiSelectField className="" name="pmBranchId" labelText="PM Branch" options={companyPMBranches} optionLabel="branchName" optionValue="branchId" onChange={handlePMBranchChange} value={selectedPMBranchIds} disabled={isCoverageDetailsVisible && !isEditable}/>
                                            </div>
                                    }
                                    {
                                        vendorRateContract.geoMapType && vendorRateContract.geoMapType === "Vendor Branch Map" &&
                                        <div className="flex flex-row col-12 md:w-5rem lg:col-2 lg:mb-0 md:mt-3 align-items-end">
                                            <Button label="Add" className="small-btn" style={{height: '34px', width: "90%"}} onClick={handleAddBranchMapping} disabled={isCoverageDetailsVisible && !isEditable}/>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                        {
                            vendorRateContract.geoMap && Object.keys(vendorRateContract.geoMap).length && sellerCompanyBranches.length ? 
                            <div className='flex flex-column pl-2' style={{width: '100%'}}>
                                <div className='mb-3' style={{fontWeight: 'bold'}}>Vendor - Procmart Branch Mapping: </div>
                                <Table 
                                    tableColumns={tableColumns}
                                    data={Object.keys(vendorRateContract.geoMap)}
                                    scrollable={"true"}
                                />
                            </div>
                            :
                            null
                        }
                        <div className="flex flex-column md:flex-row px-2 mt-5">
                            <div><span className='text-xm' style={{ color: "#333", fontSize: "12px"}}>Rate Contract Coverage Type</span><span className="required-mark"></span> :</div>
                            <div className="flex flex-column ml-3 mt-2 md:mt-0">
                                {
                                    vendorRCCoverageTypes.map(eachVendorRCCoverageType => {
                                        return (
                                            <div className="flex flex-row">
                                                <RadioButton 
                                                    inputId={eachVendorRCCoverageType.vendorRCCoverageType} 
                                                    name={eachVendorRCCoverageType.vendorRCCoverageType} 
                                                    value={eachVendorRCCoverageType.vendorRCCoverageType} 
                                                    checked={vendorRateContract.vendorRCCoverageTypeId === eachVendorRCCoverageType.vendorRCCoverageTypeId} 
                                                    onChange={handleVendorRCCoverageTypeChange}
                                                    disabled={(isCoverageDetailsVisible && !isEditable) || vendorRateContract.vendorRateContractId} // || ([2, 3].includes(vendorRateContract.vendorRCTypeId) && eachVendorRCCoverageType.vendorRCCoverageTypeId !== 4)
                                                />
                                                <label htmlFor={eachVendorRCCoverageType.vendorRCCoverageType} className="ml-1 text-xs" style={{cursor: 'pointer'}}>{eachVendorRCCoverageType.vendorRCCoverageType}</label>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                        <div className='flex flex-row justify-content-center'>
                            {
                                vendorRateContract.vendorRateContractId && 
                                <div className="flex justify-content-center mt-5 mr-3">
                                    <Button className="" severity="danger" outlined label="DELETE" onClick={handleDeleteVendorRateContract} permissions={[216]}/>
                                </div>
                            }
                            {
                                (isCoverageDetailsVisible && isEditable) &&
                                <div className="flex justify-content-center mt-5 mr-3">
                                    <Button className="small-btn discard-btn" label="DISCARD" onClick={handleDiscardVendorRateContract}/>
                                </div>
                            }
                            {
                                isCoverageDetailsVisible && !isEditable ?
                                <div className="flex justify-content-center mt-5">
                                    <Button className="" label="Edit" onClick={handleEditVendorRateContract} permissions={[214]}/>
                                </div>
                                :
                                <div className="flex justify-content-center mt-5">
                                    <Button className="" label="SAVE" onClick={handleSaveVendorRateContract} permissions={214}/>
                                </div>
                            }
                        </div>
                    </TabPanel>
                    {/* {
                        (isCoverageDetailsVisible && !isEditable) &&
                        <TabPanel header='Coverage Details'>
                            <CoverageDetails 
                                isItemsEditable={isItemsEditable}
                                materialBrands={materialBrands}
                                materialCategories={materialCategories}
                                materialDetails={materialDetails}
                                vendorRCTypes={vendorRCTypes}
                                vendorRCCoverageTypes={vendorRCCoverageTypes}
                                vendorRateContract={vendorRateContract}
                                vendorRateContractItems={vendorRateContractItems}
                                initialVendorRateContractItems={initialVendorRateContractItems}
                                setMaterialDetails={setMaterialDetails}
                                setVendorRateContractItems={setVendorRateContractItems}
                                setVendorRateContract={setVendorRateContract}
                                setInitialVendorRateContract={setInitialVendorRateContract}
                                setInitialVendorRateContractItems={setInitialVendorRateContractItems}
                                setVisibleAddVendorRateContract={setVisibleAddVendorRateContract}
                                setIsEditable={setIsEditable}
                                setIsItemsEditable={setIsItemsEditable}
                                setIsCoverageDetailsVisible={setIsCoverageDetailsVisible}
                            />
                        </TabPanel>
                    } */}
                    {
                        (isCoverageDetailsVisible && !isEditable) &&
                        <TabPanel header='Coverage Details'>
                            <CoverageDetailsNew 
                                isLoading={isLoading}
                                vendorRateContract={vendorRateContract}
                                materialBrands={materialBrands}
                                materialCategories={materialCategories}
                                vendorRCTypes={vendorRCTypes}
                                vendorRCCoverageTypes={vendorRCCoverageTypes}
                                sellerCompanies={sellerCompanies}
                                setIsLoading={setIsLoading}
                                allVendorRateContractItems={vendorRateContractItems}
                            />
                        </TabPanel>
                    }
                </TabView>
            </Dialog>
        </>
    )
}

export default PriceAndGeoMappings